import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PetitionType } from '../../types/petition';
import { MembershipType } from '../../types/member';
import FormField from '../common/FormField';
import FileUpload from '../common/FileUpload';
import { Button } from '../../design-system/components/Button';
import { Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { borders } from '../../design-system/tokens/borders';

const MAX_NOTE_LENGTH = 1000;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB

const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const IntroBlock = styled.div`
  padding: ${spacing[4]} ${spacing[5]};
  background: ${props => props.theme.colors.surfaceHover};
  border-radius: ${borders.radius.lg};
  border-left: 4px solid ${props => props.theme.colors.accent};
`;

const FormSection = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const SectionLegend = styled.legend`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  font-family: ${typography.fontFamily.primary};
  margin-bottom: ${spacing[3]};
  padding: 0;
`;

const TypeHint = styled(Text)`
  margin-top: ${spacing[1]};
  line-height: ${typography.lineHeight.relaxed};
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${spacing[3]};
  margin-top: ${spacing[2]};
  padding-top: ${spacing[6]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};

  @media (min-width: 480px) {
    flex-direction: row;
    justify-content: flex-end;
  }
`;

export interface PetitionCreateFormValues {
  petitionType: PetitionType;
  note: string;
  /** Target membership category for ChangeMembershipType petitions. */
  targetMembershipType: MembershipType | '';
  attachmentFile: File | null;
}

interface PetitionCreateFormProps {
  values: PetitionCreateFormValues;
  submitting: boolean;
  hasPendingCardReplacement?: boolean;
  onChange: (values: Partial<PetitionCreateFormValues>) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const PetitionCreateForm: React.FC<PetitionCreateFormProps> = ({
  values,
  submitting,
  hasPendingCardReplacement = false,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { petitionType, note, targetMembershipType, attachmentFile } = values;

  const isMembershipChange = petitionType === PetitionType.ChangeMembershipType;

  const noteError = useMemo(() => {
    const trimmed = note.trim();
    if (petitionType === PetitionType.LostMemberCard && !trimmed) {
      return t('petitions.reasonRequired', {
        defaultValue: 'Please provide a short reason for the card replacement.',
      });
    }
    if (isMembershipChange && !trimmed) {
      return t('petitions.membershipReasonRequired', {
        defaultValue: 'Please explain why you are requesting a membership change.',
      });
    }
    if (note.length > MAX_NOTE_LENGTH) {
      return t('petitions.reasonMaxLength', { max: MAX_NOTE_LENGTH });
    }
    return undefined;
  }, [note, petitionType, isMembershipChange, t]);

  const targetCategoryError = useMemo(() => {
    if (isMembershipChange && !targetMembershipType) {
      return t('petitions.targetMembershipRequired', {
        defaultValue: 'Please select the membership category you are requesting.',
      });
    }
    return undefined;
  }, [isMembershipChange, targetMembershipType, t]);

  const typeHintKey =
    petitionType === PetitionType.LostMemberCard
      ? 'petitions.typeHelp.lostMemberCard'
      : 'petitions.typeHelp.changeMembershipType';

  const reasonLabel =
    petitionType === PetitionType.LostMemberCard
      ? t('petitions.reasonForReplacement')
      : t('petitions.reason');

  const reasonPlaceholder =
    petitionType === PetitionType.LostMemberCard
      ? t('petitions.reasonPlaceholder')
      : t('petitions.reasonPlaceholderGeneric');

  const pendingReplacementError =
    petitionType === PetitionType.LostMemberCard && hasPendingCardReplacement
      ? t('petitions.pendingReplacementExists', {
          defaultValue: 'A card replacement petition is already pending review.',
        })
      : undefined;

  const formInvalid = !!noteError || !!targetCategoryError || !!pendingReplacementError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formInvalid) return;
    onSubmit();
  };

  return (
    <FormRoot onSubmit={handleSubmit} noValidate aria-busy={submitting}>
      <IntroBlock>
        <Text size="sm" color="secondary" style={{ lineHeight: typography.lineHeight.relaxed }}>
          {t('petitions.createPetitionDescription')}
        </Text>
      </IntroBlock>

      <FormSection aria-labelledby="petition-type-legend">
        <SectionLegend id="petition-type-legend">{t('petitions.sectionType')}</SectionLegend>
        <FormField
          id="petition-type"
          name="petitionType"
          label={t('petitions.selectPetitionType')}
          value={String(petitionType)}
          onChange={(e) => onChange({ petitionType: Number(e.target.value) as PetitionType })}
          select
          required
          options={[
            { value: String(PetitionType.LostMemberCard), label: t('petitions.types.lostMemberCard') },
            { value: String(PetitionType.ChangeMembershipType), label: t('petitions.types.changeMembershipType') },
          ]}
        />
        <TypeHint
          size="sm"
          color={pendingReplacementError ? 'error' : 'secondary'}
          as="p"
          style={{ margin: 0 }}
        >
          {pendingReplacementError || t(typeHintKey)}
        </TypeHint>
      </FormSection>

      <FormSection aria-labelledby="petition-details-legend">
        <SectionLegend id="petition-details-legend">{t('petitions.sectionDetails')}</SectionLegend>
        {isMembershipChange && (
          <FormField
            id="petition-target-membership"
            name="targetMembershipType"
            label={t('petitions.targetMembershipType', {
              defaultValue: 'Requested membership category',
            })}
            select
            required
            value={targetMembershipType === '' ? '' : String(targetMembershipType)}
            onChange={(e) =>
              onChange({
                targetMembershipType: e.target.value
                  ? (Number(e.target.value) as MembershipType)
                  : '',
              })
            }
            options={[
              { value: '', label: t('petitions.selectMembershipType', { defaultValue: 'Select category' }) },
              { value: String(MembershipType.Regular), label: t('membershipType.regular') },
              { value: String(MembershipType.Student), label: t('membershipType.student') },
              { value: String(MembershipType.Retired), label: t('membershipType.retired') },
            ]}
            error={targetCategoryError}
            helperText={t('petitions.targetMembershipHint', {
              defaultValue: 'Choose the membership category you want to move to.',
            })}
          />
        )}
        <FormField
          id="petition-note"
          name="note"
          label={reasonLabel}
          value={note}
          onChange={(e) => onChange({ note: e.target.value })}
          placeholder={reasonPlaceholder}
          textarea
          required
          helperText={
            petitionType === PetitionType.LostMemberCard
              ? t('petitions.reasonRequiredHint', {
                  defaultValue: 'A short reason is required for card replacement requests.',
                })
              : t('petitions.membershipReasonRequiredHint', {
                  defaultValue: 'A short explanation is required for membership-change requests.',
                })
          }
          error={noteError}
          maxLength={MAX_NOTE_LENGTH + 50}
        />
      </FormSection>

      {isMembershipChange && (
        <FormSection aria-labelledby="petition-attachment-legend">
          <SectionLegend id="petition-attachment-legend">{t('petitions.sectionAttachment')}</SectionLegend>
          <FileUpload
            label={t('petitions.attachmentLabel')}
            hint={t('petitions.attachmentHint')}
            currentFile={attachmentFile}
            onFileSelect={(file) => onChange({ attachmentFile: file })}
            onFileRemove={() => onChange({ attachmentFile: null })}
            maxSize={MAX_ATTACHMENT_SIZE}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
        </FormSection>
      )}

      <Actions>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={submitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={submitting || formInvalid}>
          {submitting ? t('common.submitting') : t('petitions.submit')}
        </Button>
      </Actions>
    </FormRoot>
  );
};

export default PetitionCreateForm;
